import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Card, Button, Modal, Form, Input, message, Tag, Space,
    Typography, Descriptions, Tabs, DatePicker, Row, Col, Statistic,
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
    SearchOutlined, DownloadOutlined, ReloadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatVND = (amount) =>
    new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

const formatDateTime = (date) =>
    date ? dayjs(date).format('DD/MM/YYYY HH:mm:ss') : '—';

const STATUS_TAG = {
    success:        { color: 'success', text: 'Thành công' },
    pending_review: { color: 'warning', text: 'Chờ xử lý' },
    failed:         { color: 'error',   text: 'Thất bại' },
};

const getStatusTag = (status) => {
    const cfg = STATUS_TAG[status] || { color: 'default', text: status };
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
};

// ─── Tab 1: Pending ───────────────────────────────────────────────────────────

const PendingTab = () => {
    const [loading, setLoading]           = useState(false);
    const [data, setData]                 = useState([]);
    const [search, setSearch]             = useState('');
    const [pagination, setPagination]     = useState({ current: 1, pageSize: 15, total: 0 });
    const [selectedLog, setSelectedLog]   = useState(null);
    const [detailOpen, setDetailOpen]     = useState(false);
    const [processOpen, setProcessOpen]   = useState(false);
    const [form]                          = Form.useForm();
    const [messageApi, contextHolder]     = message.useMessage();

    const fetch = useCallback(async (page = 1, pageSize = 15, searchVal = search) => {
        try {
            setLoading(true);
            const params = { page, per_page: pageSize };
            if (searchVal) params.search = searchVal;

            const res = await axiosInstance.get('/api/v1/admin/payment-logs/pending', { params });
            if (res.data.success) {
                setData(res.data.data);
                setPagination({
                    current:  res.data.meta.current_page,
                    pageSize: res.data.meta.per_page,
                    total:    res.data.meta.total,
                });
            }
        } catch {
            messageApi.error('Không thể tải danh sách chờ xử lý!');
        } finally {
            setLoading(false);
        }
    }, [search, messageApi]);

    useEffect(() => { fetch(1, pagination.pageSize, ''); }, []);

    const handleSearch = (val) => {
        setSearch(val);
        fetch(1, pagination.pageSize, val);
    };

    const handleProcess = (record, action) => {
        setSelectedLog(record);
        form.setFieldsValue({ action, cccd: record.cccd_ref || '', note: '' });
        setProcessOpen(true);
    };

    const handleProcessSubmit = async (values) => {
        try {
            const res = await axiosInstance.post(
                `/api/v1/admin/payment-logs/${selectedLog.id}/process`,
                values
            );
            if (res.data.success) {
                messageApi.success('Xử lý thành công!');
                setProcessOpen(false);
                form.resetFields();
                fetch(pagination.current, pagination.pageSize);
            }
        } catch {
            messageApi.error('Không thể xử lý payment log!');
        }
    };

    const columns = [
        { title: 'Mã giao dịch', dataIndex: 'bank_trans_id', width: 160 },
        {
            title: 'CCCD',
            dataIndex: 'cccd_ref',
            width: 130,
            render: (v) => v || <Text type="secondary">Chưa xác định</Text>,
        },
        {
            title: 'Số tiền',
            dataIndex: 'amount',
            width: 130,
            render: (v) => formatVND(v),
        },
        { title: 'Nội dung', dataIndex: 'raw_content', ellipsis: true },
        {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            width: 160,
            render: formatDateTime,
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 220,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => { setSelectedLog(record); setDetailOpen(true); }}
                    >Chi tiết</Button>
                    {record.status === 'pending_review' && (
                        <>
                            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                                onClick={() => handleProcess(record, 'approve')}>Duyệt</Button>
                            <Button size="small" danger icon={<CloseCircleOutlined />}
                                onClick={() => handleProcess(record, 'reject')}>Từ chối</Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}
            <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
                <Col flex="1">
                    <Input.Search
                        placeholder="Tìm theo CCCD, mã giao dịch, nội dung..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        onSearch={handleSearch}
                        style={{ maxWidth: 420 }}
                    />
                </Col>
                <Col>
                    <Button icon={<ReloadOutlined />} onClick={() => fetch(1, pagination.pageSize, search)}>
                        Làm mới
                    </Button>
                </Col>
            </Row>

            <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 900 }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50'],
                    showTotal: (t, r) => `${r[0]}–${r[1]} / ${t} bản ghi`,
                    onChange: (page, pageSize) => fetch(page, pageSize),
                    onShowSizeChange: (_, pageSize) => fetch(1, pageSize),
                }}
            />

            {/* Detail Modal */}
            <Modal
                title="Chi tiết Payment Log"
                open={detailOpen}
                onCancel={() => setDetailOpen(false)}
                footer={<Button onClick={() => setDetailOpen(false)}>Đóng</Button>}
                width={800}
            >
                {selectedLog && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
                        <Descriptions.Item label="Mã giao dịch NH">{selectedLog.bank_trans_id}</Descriptions.Item>
                        <Descriptions.Item label="Transaction ID">{selectedLog.transaction_id}</Descriptions.Item>
                        <Descriptions.Item label="Số CCCD">{selectedLog.cccd_ref || <Text type="secondary">Chưa xác định</Text>}</Descriptions.Item>
                        <Descriptions.Item label="Số tiền">{formatVND(selectedLog.amount)}</Descriptions.Item>
                        <Descriptions.Item label="Số tài khoản">{selectedLog.account_number || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Nội dung gốc">{selectedLog.raw_content}</Descriptions.Item>
                        <Descriptions.Item label="Ngày giao dịch">{formatDateTime(selectedLog.trans_date)}</Descriptions.Item>
                        <Descriptions.Item label="Trạng thái">{getStatusTag(selectedLog.status)}</Descriptions.Item>
                        <Descriptions.Item label="Ghi chú">{selectedLog.note || <Text type="secondary">Không có</Text>}</Descriptions.Item>
                        <Descriptions.Item label="Student Profile ID">{selectedLog.student_profile_id || <Text type="secondary">Chưa liên kết</Text>}</Descriptions.Item>
                        <Descriptions.Item label="Raw Payload">
                            <pre style={{ maxHeight: 200, overflow: 'auto', background: '#f5f5f5', padding: 10, borderRadius: 4 }}>
                                {JSON.stringify(selectedLog.raw_payload, null, 2)}
                            </pre>
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Process Modal */}
            <Modal
                title={form.getFieldValue('action') === 'approve' ? 'Duyệt Payment Log' : 'Từ chối Payment Log'}
                open={processOpen}
                onCancel={() => { setProcessOpen(false); form.resetFields(); }}
                onOk={() => form.submit()}
                okText="Xác nhận"
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical" onFinish={handleProcessSubmit}>
                    <Form.Item name="action" hidden><Input /></Form.Item>
                    {form.getFieldValue('action') === 'approve' && (
                        <Form.Item
                            name="cccd"
                            label="Số CCCD"
                            rules={[
                                { required: true, message: 'Vui lòng nhập số CCCD!' },
                                { len: 12, message: 'CCCD phải có 12 chữ số!' },
                                { pattern: /^[0-9]{12}$/, message: 'CCCD chỉ chứa chữ số!' },
                            ]}
                        >
                            <Input placeholder="Nhập 12 chữ số CCCD" maxLength={12} />
                        </Form.Item>
                    )}
                    <Form.Item name="note" label="Ghi chú">
                        <TextArea rows={4} placeholder="Nhập ghi chú (tùy chọn)" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

// ─── Tab 2: Successful ────────────────────────────────────────────────────────

const SuccessTab = () => {
    const [loading, setLoading]       = useState(false);
    const [exporting, setExporting]   = useState(false);
    const [data, setData]             = useState([]);
    const [search, setSearch]         = useState('');
    const [dateRange, setDateRange]   = useState([null, null]);
    const [sortOrder, setSortOrder]   = useState('desc');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [messageApi, contextHolder] = message.useMessage();

    const buildParams = useCallback((page, pageSize, searchVal, range, sort) => {
        const params = { page, per_page: pageSize, sort };
        if (searchVal) params.search = searchVal;
        if (range?.[0]) params.date_from = range[0].format('YYYY-MM-DD');
        if (range?.[1]) params.date_to   = range[1].format('YYYY-MM-DD');
        return params;
    }, []);

    const fetch = useCallback(async (
        page      = 1,
        pageSize  = pagination.pageSize,
        searchVal = search,
        range     = dateRange,
        sort      = sortOrder,
    ) => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/v1/admin/payment-logs/success', {
                params: buildParams(page, pageSize, searchVal, range, sort),
            });
            if (res.data.success) {
                setData(res.data.data);
                setPagination({
                    current:  res.data.meta.current_page,
                    pageSize: res.data.meta.per_page,
                    total:    res.data.meta.total,
                });
            }
        } catch {
            messageApi.error('Không thể tải danh sách đã thanh toán!');
        } finally {
            setLoading(false);
        }
    }, [search, dateRange, sortOrder, pagination.pageSize, buildParams, messageApi]);

    useEffect(() => { fetch(); }, []);

    const handleExport = async () => {
        try {
            setExporting(true);
            const res = await axiosInstance.get('/api/v1/admin/payment-logs/export-success', {
                params: buildParams(1, 9999, search, dateRange, sortOrder),
            });

            if (!res.data.success || !res.data.data.length) {
                messageApi.warning('Không có dữ liệu để xuất!');
                return;
            }

            const rows = res.data.data.map((item, idx) => ({
                'STT':                  idx + 1,
                'Mã giao dịch NH':      item.bank_trans_id || '',
                'CCCD':                 item.cccd_ref || '',
                'Họ tên thí sinh':      item.student_profile?.full_name || '',
                'Số tiền (VNĐ)':        Number(item.amount),
                'Nội dung chuyển khoản': item.raw_content || '',
                'Ngày giao dịch':       item.trans_date ? dayjs(item.trans_date).format('DD/MM/YYYY HH:mm:ss') : '',
                'Ghi chú':              item.note || '',
            }));

            const ws = XLSX.utils.json_to_sheet(rows);
            ws['!cols'] = [
                { wch: 5 }, { wch: 20 }, { wch: 14 }, { wch: 30 },
                { wch: 15 }, { wch: 40 }, { wch: 22 }, { wch: 30 },
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Da thanh toan');
            XLSX.writeFile(wb, `thanh-toan-bidv-${dayjs().format('YYYYMMDD-HHmm')}.xlsx`);
            messageApi.success(`Đã xuất ${rows.length} bản ghi!`);
        } catch {
            messageApi.error('Xuất Excel thất bại!');
        } finally {
            setExporting(false);
        }
    };

    const handleSortToggle = () => {
        const next = sortOrder === 'desc' ? 'asc' : 'desc';
        setSortOrder(next);
        fetch(1, pagination.pageSize, search, dateRange, next);
    };

    const columns = [
        {
            title: '#',
            width: 50,
            render: (_, __, i) => (pagination.current - 1) * pagination.pageSize + i + 1,
        },
        { title: 'Mã giao dịch NH', dataIndex: 'bank_trans_id', width: 170 },
        {
            title: 'CCCD',
            dataIndex: 'cccd_ref',
            width: 130,
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: 'Họ tên thí sinh',
            width: 200,
            render: (_, r) => r.student_profile?.full_name
                ? <Text strong>{r.student_profile.full_name}</Text>
                : <Text type="secondary">—</Text>,
        },
        {
            title: 'Số tiền',
            dataIndex: 'amount',
            width: 130,
            render: (v) => <Text strong style={{ color: '#27ae60' }}>{formatVND(v)}</Text>,
        },
        { title: 'Nội dung CK', dataIndex: 'raw_content', ellipsis: true },
        {
            title: (
                <Space
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={handleSortToggle}
                >
                    Ngày giao dịch
                    <span style={{ fontSize: 11, color: '#1890ff' }}>
                        {sortOrder === 'desc' ? '↓ Mới nhất' : '↑ Cũ nhất'}
                    </span>
                </Space>
            ),
            dataIndex: 'trans_date',
            width: 170,
            render: formatDateTime,
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            ellipsis: true,
            width: 200,
            render: (v) => v || <Text type="secondary">—</Text>,
        },
    ];

    const totalAmount = data.reduce((sum, r) => sum + Number(r.amount || 0), 0);

    return (
        <>
            {contextHolder}

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                        <Statistic
                            title="Trang hiện tại"
                            value={data.length}
                            suffix="giao dịch"
                            valueStyle={{ color: '#27ae60', fontSize: 18 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                        <Statistic
                            title="Tổng cộng (trang này)"
                            value={totalAmount}
                            suffix="₫"
                            valueStyle={{ color: '#27ae60', fontSize: 18 }}
                            formatter={(v) => Number(v).toLocaleString('vi-VN')}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic
                            title="Tổng số bản ghi"
                            value={pagination.total}
                            suffix="giao dịch"
                            valueStyle={{ fontSize: 18 }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Toolbar */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle" wrap>
                <Col flex="1" style={{ minWidth: 220 }}>
                    <Input.Search
                        placeholder="Tìm CCCD, mã GD, tên thí sinh..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        onSearch={(val) => { setSearch(val); fetch(1, pagination.pageSize, val, dateRange, sortOrder); }}
                        style={{ maxWidth: 380 }}
                    />
                </Col>
                <Col>
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Từ ngày', 'Đến ngày']}
                        onChange={(dates) => {
                            setDateRange(dates || [null, null]);
                            fetch(1, pagination.pageSize, search, dates || [null, null], sortOrder);
                        }}
                    />
                </Col>
                <Col>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetch(1, pagination.pageSize, search, dateRange, sortOrder)}
                    >
                        Làm mới
                    </Button>
                </Col>
                <Col>
                    <Button
                        icon={<DownloadOutlined />}
                        loading={exporting}
                        onClick={handleExport}
                        style={{ color: '#fff', background: '#27ae60', borderColor: '#27ae60' }}
                    >
                        Xuất Excel
                    </Button>
                </Col>
            </Row>

            <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1100 }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50'],
                    showTotal: (t, r) => `${r[0]}–${r[1]} / ${t} bản ghi`,
                    onChange: (page, pageSize) => fetch(page, pageSize),
                    onShowSizeChange: (_, pageSize) => fetch(1, pageSize),
                }}
            />
        </>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PaymentLogsManagement = () => (
    <AdminLayout title="Thanh toán BIDV">
        <Card>
            <Title level={4} style={{ marginBottom: 20 }}>Quản lý thanh toán BIDV</Title>
            <Tabs
                defaultActiveKey="pending"
                type="card"
                items={[
                    {
                        key:      'pending',
                        label:    'Chờ xử lý',
                        children: <PendingTab />,
                    },
                    {
                        key:      'success',
                        label:    'Đã thanh toán thành công',
                        children: <SuccessTab />,
                    },
                ]}
            />
        </Card>
    </AdminLayout>
);

export default PaymentLogsManagement;
